/** Pure presentation rules for exports and notifications: honest states, never optimistic (V08-A08, V08-A12, UX11). */
import type { ApiErrorBody } from "../../contracts/envelope.ts";
import type {
  DeliveryReason,
  DeliveryRow,
  DeliveryState,
  ExportJobRow,
  JobFailure,
  JobState,
  QuietHoursRow,
} from "../../contracts/delivery.ts";

export type Tone = "ok" | "warn" | "bad" | "info";

export interface Presentation {
  readonly label: string;
  readonly tone: Tone;
  readonly note: string;
}

const JOB_TEXT: Readonly<Record<JobState, Presentation>> = {
  QUEUED: { label: "Queued", tone: "info", note: "Waiting to run." },
  RUNNING: {
    label: "Running",
    tone: "info",
    note: "Being generated; nothing is downloadable yet.",
  },
  SUCCEEDED: {
    label: "Succeeded",
    tone: "ok",
    note: "Ready. Every download re-checks your access.",
  },
  FAILED: { label: "Failed", tone: "bad", note: "No artifact was kept." },
  CANCEL_REQUESTED: {
    label: "Cancel requested",
    tone: "warn",
    note: "Cancellation is requested, not done: the job stops at its next chunk.",
  },
  CANCELLED: {
    label: "Cancelled",
    tone: "warn",
    note: "Stopped; the partial artifact was dropped.",
  },
  EXPIRED: {
    label: "Expired",
    tone: "warn",
    note: "Past its 24-hour life; the artifact was purged.",
  },
};

const FAILURE_TEXT: Readonly<Record<JobFailure, string>> = {
  GRANT_REVOKED: "your grant no longer covered the scope when the job ran",
  LICENCE_HELD: "the data licence held the export back when the job ran",
  SOURCE_ERROR: "the data source failed",
  ROW_TOO_LARGE: "a row was too large for a chunk",
  ROW_LIMIT: "the report passed its row budget",
};

export interface JobView extends Presentation {
  readonly cancellable: boolean;
  readonly downloadable: boolean;
}

/** How a job is shown: its state in words, whether it can be cancelled, whether it can be downloaded. */
export function jobPresentation(row: ExportJobRow): JobView {
  const base = JOB_TEXT[row.state];
  const note =
    row.failure === null ? base.note : `${base.note} Reason: ${FAILURE_TEXT[row.failure]}.`;
  return {
    ...base,
    note,
    cancellable: row.state === "QUEUED" || row.state === "RUNNING",
    downloadable: row.state === "SUCCEEDED",
  };
}

const DELIVERY_TEXT: Readonly<Record<DeliveryState, Presentation>> = {
  INTENDED: { label: "Queued", tone: "info", note: "Written down, not sent yet." },
  SENT: {
    label: "Sent, not accepted",
    tone: "info",
    note: "Handed to the provider; it has not said it accepted the message.",
  },
  ACCEPTED: {
    label: "Accepted by provider, not confirmed",
    tone: "warn",
    note: "The provider took the message. That is not receipt: delivery is confirmed only by a verified callback.",
  },
  DELIVERED: {
    label: "Delivered",
    tone: "ok",
    note: "Confirmed by a verified provider callback.",
  },
  FAILED: { label: "Failed", tone: "bad", note: "Not delivered." },
  EXPIRED: {
    label: "Expired, never confirmed",
    tone: "warn",
    note: "The message expired before delivery was confirmed; it may or may not have arrived.",
  },
  SUPERSEDED: { label: "Not sent", tone: "info", note: "Replaced before it was sent." },
};

const REASON_TEXT: Readonly<Record<DeliveryReason, string>> = {
  NEWER_VERSION: "a newer version of the recommendation replaced it",
  DIGESTED: "it was folded into a digest (more than 12 non-critical messages in an hour)",
  EXPIRED: "it expired",
  SEND_FAILED: "the provider refused it",
  SEND_TIMEOUT: "the provider gave no answer in time; it may have been sent",
  PROVIDER_FAILED: "the provider reported failure",
  NOT_ENTITLED: "the recipient was not entitled to it at send time",
  LIMITED: "the channel's hourly limit was reached; folded into the next digest",
};

export interface DeliveryView extends Presentation {
  readonly confirmed: boolean;
}

/** How a delivery is shown: only `DELIVERED` is confirmed; ignored callbacks are disclosed. */
export function deliveryPresentation(row: DeliveryRow): DeliveryView {
  const base = DELIVERY_TEXT[row.state];
  const parts = [base.note];
  if (row.reason !== null) {
    parts.push(`Reason: ${REASON_TEXT[row.reason]}.`);
  }
  if (row.lastIgnored !== null) {
    parts.push(
      `${String(row.ignoredCallbacks)} provider callback${row.ignoredCallbacks === 1 ? "" : "s"} ignored (latest: ${row.lastIgnored}); an unverifiable callback never confirms delivery.`,
    );
  }
  return { ...base, note: parts.join(" "), confirmed: row.state === "DELIVERED" };
}

/** Minutes after midnight as `HH:MM`. */
export function clockText(minuteOfDay: number): string {
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `HH:MM` as minutes after midnight, or `null` when it is not a valid time. */
export function parseClock(text: string): number | null {
  const found = CLOCK.exec(text);
  return found === null ? null : Number(found[1]) * 60 + Number(found[2]);
}

/** The quiet-hours window in words (IST); it always says what it cannot silence. */
export function windowText(row: QuietHoursRow): string {
  const silenced = row.enabled
    ? `Non-critical messages are held from ${clockText(row.startMinute)} to ${clockText(row.endMinute)} IST.`
    : "Quiet hours are off.";
  return `${silenced} SEV1 and mandatory escalation are never silenced.`;
}

const REFUSALS: Readonly<Record<string, string>> = {
  GrantRefusedError: "your grant no longer covers this export's scope",
  LicenceRefusedError: "the data licence now holds this export back",
  JobExpiredError: "the export has expired and its artifact was purged",
  ManifestMismatchError: "the artifact does not match its manifest (mode, fidelity or bytes)",
};

/** A refused download in words; it always says that no file was saved (UX11). */
export function refusalMessage(status: number, body: ApiErrorBody | null): string {
  const kind = typeof body?.kind === "string" ? body.kind : "";
  const why =
    REFUSALS[kind] ??
    (status === 404 ? "there is no such export" : `the server refused (status ${String(status)})`);
  return `Download refused: ${why}. No file was saved.`;
}

/** The SHA-256 of bytes as lowercase hex. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as Uint8Array<ArrayBuffer>);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
